import { memo, type ReactNode } from 'react';
import Markdown, { type Components, type Options } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

/**
 * Shared by both components below so an inbox row and the thread it opens agree
 * on what counts as formatting. `singleTilde: false` matches the Stream SDK's
 * `renderText`, which is what renders the message on the candidate side: `~one~`
 * is not strikethrough.
 *
 * `remarkBreaks` is the one deliberate departure from that renderer. A recruiter
 * pressing shift+enter means a line break, not the space CommonMark gives a soft
 * break. stream-chat still collapses those, so the same message is shaped
 * differently there for as long as both apps are live. See docs/TODO.md.
 */
const SHARED_OPTIONS = {
  remarkPlugins: [[remarkGfm, { singleTilde: false }], remarkBreaks],
  unwrapDisallowed: true,
} satisfies Options;

/**
 * The SDK's `defaultAllowedTagNames`, minus its emoji and mention renderers.
 * Headings, images and rules are left out on purpose: a bubble is not a
 * document, so they unwrap to their text.
 */
const ALLOWED_ELEMENTS = [
  'br',
  'p',
  'em',
  'strong',
  'a',
  'ol',
  'ul',
  'li',
  'code',
  'pre',
  'blockquote',
  'del',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

/** A link whose protocol the URL transform rejected arrives with no href, and
 *  stays text. */
const Anchor: Components['a'] = ({ children, href }) =>
  href ? (
    <a href={href} target="_blank" rel="nofollow noreferrer noopener">
      {children}
    </a>
  ) : (
    <>{children}</>
  );

const RICH_COMPONENTS: Components = { a: Anchor };

/** Memoised because react-markdown re-parses on every render, and both the
 *  thread and the inbox list re-render on every Stream event. */
export const MessageMarkdown = memo(function MessageMarkdown({ text }: { text: string }) {
  return (
    <Markdown {...SHARED_OPTIONS} allowedElements={ALLOWED_ELEMENTS} components={RICH_COMPONENTS}>
      {text}
    </Markdown>
  );
});

/** The trailing space stops one block's text running into the next once the
 *  formatting around them is gone. */
const InlineSpacer = ({ children }: { children?: ReactNode }) => <>{children} </>;

const PLAIN_COMPONENTS: Components = {
  br: InlineSpacer,
  p: InlineSpacer,
  li: InlineSpacer,
  blockquote: InlineSpacer,
  td: InlineSpacer,
  th: InlineSpacer,
};

const BLOCK_SEPARATOR_TAGS = Object.keys(PLAIN_COMPONENTS);

/** The same text flattened for an inbox row, so no block element survives to
 *  escape the row's ellipsis. */
export const PlainMessageText = memo(function PlainMessageText({ text }: { text: string }) {
  return (
    <Markdown
      {...SHARED_OPTIONS}
      allowedElements={BLOCK_SEPARATOR_TAGS}
      components={PLAIN_COMPONENTS}
    >
      {text}
    </Markdown>
  );
});
